// Single entry point for every dataLayer event. Named neutrally (not "tracking"
// / "analytics") because some ad-blocker filter lists match those paths.
//
// window.vsTracking is bootstrapped in Tracking.php:
//  - debug (local/development): GTM is never loaded, so events are only logged
//    to the console to show what *would* be sent.
//  - log (every environment, production included): events are logged to the
//    console, with the environment and whether they reached GTM.
//  - environment: added to every event so staging can be filtered in GTM / GA4.

export function pushEvent(event, payload = {}) {
    const config = window.vsTracking || {};
    window.dataLayer = window.dataLayer || [];

    // GA4 recommendation: clear the previous ecommerce object before a new one.
    if (payload.ecommerce) window.dataLayer.push({ ecommerce: null });

    const entry = { event, environment: config.environment, ...payload };
    window.dataLayer.push(entry);

    if (config.log) {
        const consent = window.tarteaucitron && window.tarteaucitron.state
            ? window.tarteaucitron.state.vsgtm
            : undefined;
        // Events pushed on page load usually run before tarteaucitron has read
        // the stored choice: consent is then undefined (unknown), not refused.
        let status;
        if (config.debug) status = 'GTM not loaded in this environment, nothing is sent.';
        else if (window.google_tag_manager) status = 'Sent to GTM.';
        else if (consent === true) status = 'Queued: GTM is loading, the event will be replayed once it is.';
        else if (consent === false) status = 'Queued: statistics cookies refused, GTM not loaded. Replayed only if they are accepted on this page.';
        else status = 'Queued: GTM not loaded yet (consent not read yet). Replayed when GTM loads, if statistics cookies are accepted.';

        console.groupCollapsed(`%c[tracking] ${event} %c${config.environment || '?'}`, 'color:#0a7;font-weight:bold', 'color:#888;font-weight:normal');
        console.log('payload', entry);
        console.log(status, '| environment:', config.environment, '| consent (statistics):', consent);
        console.groupEnd();
    }
}

export function currency() {
    return (window.vsTracking && window.vsTracking.currency) || 'EUR';
}

// Item lists: the list an item was last picked from (select_item), keyed by
// item_id, so the view_item / add_to_cart / checkout / purchase that follow
// carry the same item_list_name. Last click wins; entries expire after a day.
const LISTS_KEY = 'vs_item_lists';
const LIST_TTL = 24 * 60 * 60 * 1000;

function readLists() {
    try {
        const lists = JSON.parse(localStorage.getItem(LISTS_KEY)) || {};
        const now = Date.now();
        Object.keys(lists).forEach((id) => {
            if (!lists[id] || now - lists[id].ts > LIST_TTL) delete lists[id];
        });
        return lists;
    } catch (e) {
        return {};
    }
}

function writeLists(lists) {
    try {
        localStorage.setItem(LISTS_KEY, JSON.stringify(lists));
    } catch (e) {}
}

function rememberList(itemId, list) {
    const lists = readLists();
    lists[itemId] = { list, ts: Date.now() };
    writeLists(lists);
}

// Once purchased, an item starts over: its next purchase isn't credited to an old list.
export function forgetLists(items) {
    const lists = readLists();
    items.forEach((item) => delete lists[item.item_id]);
    writeLists(lists);
}

export function withList(item) {
    if (!item || item.item_list_name) return item;
    const entry = readLists()[item.item_id];
    return entry ? { ...item, item_list_id: entry.list, item_list_name: entry.list } : item;
}

// Events tied to a single item: category is also exposed at the top level so a
// GTM trigger / variable can read it without digging into ecommerce.items.
export function pushItemEvent(event, item, extra = {}) {
    item = withList(item);
    pushEvent(event, {
        item_category: item.item_category,
        ecommerce: {
            currency: currency(),
            value: item.price * item.quantity,
            items: [item],
            ...extra,
        },
    });
}

// A click on a card / CTA ([data-tracking-item]) inside a product list
// ([data-item-list], see the twig templates): GA4 select_item, and the list is
// remembered for that item. Capture phase: some CTAs stop propagation.
export function initItemListEvents() {
    document.addEventListener(
        'click',
        (e) => {
            const el = e.target.closest('[data-tracking-item]');
            const listEl = el && el.closest('[data-item-list]');
            if (!listEl) return;

            // Only real actions: a link, an add-to-cart CTA, the upsell checkbox,
            // a clickable push. Not a close button, a disabled CTA or a slider drag area.
            const action = e.target.closest('a[href], button, input, .is-clickable');
            if (!action || !el.contains(action)) return;
            if (action.closest('.is-disabled, [aria-disabled], [class*="__close"]')) return;

            let item;
            try {
                item = JSON.parse(el.dataset.trackingItem);
            } catch (err) {
                return;
            }

            const list = listEl.dataset.itemList;
            rememberList(item.item_id, list);
            pushEvent('select_item', {
                item_category: item.item_category,
                ecommerce: {
                    item_list_id: list,
                    item_list_name: list,
                    items: [{ ...item, item_list_id: list, item_list_name: list }],
                },
            });
        },
        true
    );
}

// Whole-cart events (view_cart, begin_checkout, add_payment_info). `cart` is
// Tracking::cart_payload(): items keyed by product id, value excl. tax after
// coupons, applied coupons.
export function pushCartEvent(event, cart, extra = {}) {
    const items = Object.values((cart && cart.items) || {}).map(withList);
    if (!items.length) return;

    pushEvent(event, {
        ecommerce: {
            currency: currency(),
            value: cart.value,
            ...(cart.coupon ? { coupon: cart.coupon } : {}),
            items,
            ...extra,
        },
    });
}

// Codes of a comma-separated coupon list, lowercased like WooCommerce stores them.
export function couponCodes(list) {
    return String(list || '').split(',').map((c) => c.trim().toLowerCase()).filter(Boolean);
}

// apply_coupon (site event, no GA4 equivalent): a valid code was just applied,
// from the side cart or the checkout. Not sent for a rejected or removed code,
// nor for a code already applied when the page loaded. `value` is the cart
// total excl. tax after the coupon, `discount` the whole cart discount excl. tax.
export function pushCouponEvent(coupon, value, discount) {
    pushEvent('apply_coupon', {
        coupon,
        currency: currency(),
        value,
        discount,
    });
}

// Parameter carrying the value of each server-queued event.
const QUEUED_EVENT_PARAMS = {
    generate_lead: 'lead_source',
    sign_up: 'method',
};

// Events the server queued (Tracking::queue_event: `vs_events` cookie of
// "event:value" entries): generate_lead for a new newsletter signup, sign_up
// for a new account. Run on every page load (checkout redirect) and right
// after an AJAX signup.
export function flushQueuedEvents() {
    const match = document.cookie.match(/(?:^|;\s*)vs_events=([^;]*)/);
    if (!match) return;

    document.cookie = 'vs_events=; path=/; max-age=0; samesite=lax';
    decodeURIComponent(match[1]).split(',').filter(Boolean).forEach((entry) => {
        const [event, value] = entry.split(':');
        const param = QUEUED_EVENT_PARAMS[event];
        if (param && value) pushEvent(event, { [param]: value });
    });
}

// Events tied to the page itself, from the server bootstrap (Tracking.php).
// Every navigation is a full page load, so this runs once per page.
export function initPageEvents() {
    const config = window.vsTracking || {};

    if (config.viewItem) pushItemEvent('view_item', config.viewItem);
    if (config.isCheckout) pushCartEvent('begin_checkout', config.cart);
    flushQueuedEvents();
}

// "Live demo" link on product pages (item serialised in data-tracking-item).
export function initLiveDemoEvents() {
    document.addEventListener('click', (e) => {
        const link = e.target.closest('.product__infos__demoLink[data-tracking-item]');
        if (!link) return;

        try {
            pushItemEvent('live_demo', JSON.parse(link.dataset.trackingItem));
        } catch (err) {}
    });
}
